import React, { useState } from "react";
import { SHOP_PIN_COLORS } from "./shopMapPopupCard";
import { SpaLink } from "./MarketingLayout";
import { FooterStoreLinks } from "./StoreLinks";
import { isNativePlatform } from "../utils/platform";
import VideoEmbed from "./VideoEmbed";

// Local Material Symbols wrapper. (The shared ./Icon is a Flaticon wrapper; Material Symbols
// gives reliable, well-known glyph names and is already loaded globally via index.css.)
function Icon({ name, className = "" }) {
  return (
    <span className={`material-symbols-outlined ${className}`} aria-hidden="true">
      {name}
    </span>
  );
}

function Pill({ children, className = "" }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${className}`}
    >
      {children}
    </span>
  );
}

// A miniature of the real ShopMatchCard, used to make the hero feel like the actual product.
function PreviewShopCard({ index, name, location, score, accent, badges, subline }) {
  return (
    <div
      className="rounded-lg border border-l-4 border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 shadow-sm"
      style={{ borderLeftColor: accent }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-slate-900 dark:text-gray-100">
            {index}. {name}
          </div>
          <div className="text-xs text-slate-500 dark:text-gray-400">{location}</div>
          {subline ? (
            <div className="text-xs font-medium" style={{ color: SHOP_PIN_COLORS.mobile }}>
              {subline}
            </div>
          ) : null}
        </div>
        <div className="whitespace-nowrap text-sm font-semibold text-blue-700 dark:text-blue-300">{score}</div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {badges.map((b) => (
          <span
            key={b}
            className="rounded-full border border-slate-200 dark:border-gray-700 px-2 py-0.5 text-[11px] text-slate-600 dark:text-gray-300"
          >
            {b}
          </span>
        ))}
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, children, accent = "text-blue-700 dark:text-blue-300", bg = "bg-blue-100 dark:bg-blue-950/40" }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
      <div
        className={`mb-4 flex h-12 w-12 items-center justify-center rounded-xl ${bg} ${accent}`}
      >
        <Icon name={icon} className="text-2xl" />
      </div>
      <h3 className="mb-1.5 text-lg font-semibold text-slate-900 dark:text-gray-100">{title}</h3>
      <p className="text-sm leading-relaxed text-slate-600 dark:text-gray-300">{children}</p>
    </div>
  );
}

export default function LandingPage({ onGoLogin, onGoSignup, onGoContact, onNavigate = () => {} }) {
  const year = new Date().getFullYear();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Hidden inside the installed app - see the same guard in MarketingLayout.
  const showAppLinks = !isNativePlatform();

  return (
    <div className="min-h-screen bg-white dark:bg-gray-800 text-slate-900 dark:text-gray-100">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-slate-200 dark:border-gray-700 bg-white/85 dark:bg-gray-800/85 backdrop-blur">
        <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between gap-2 px-4">
          {/* min-w-0 + truncate is the guard, not the fix: a flex item defaults to
              min-width:auto, so without min-w-0 this link refuses to shrink below the
              wordmark's width and the text paints straight over the buttons. */}
          <a href="#top" className="flex min-w-0 items-center gap-2">
            <img src="/logo-192.png" alt="" className="h-8 w-8 shrink-0 rounded-lg" />
            <span className="truncate text-base font-bold tracking-tight text-slate-900 dark:text-gray-100 md:text-lg">GearCheckAuto</span>
          </a>
          <nav aria-label="Main" className="hidden items-center gap-7 md:flex">
            <a href="#how-it-works" className="text-sm font-medium text-slate-600 dark:text-gray-300 hover:text-blue-700">How it works</a>
            <a href="#owners" className="text-sm font-medium text-slate-600 dark:text-gray-300 hover:text-blue-700">For owners</a>
            <a href="#shops" className="text-sm font-medium text-slate-600 dark:text-gray-300 hover:text-blue-700">For shops</a>
            <button type="button" onClick={onGoContact} className="text-sm font-medium text-slate-600 dark:text-gray-300 hover:text-blue-700">Contact us</button>
          </nav>
          {/* At 360px the wordmark + Login + Get started + menu button need ~370px of the 328px
              available, so Login moves into the mobile menu below and the primary CTA stays. */}
          <div className="flex shrink-0 items-center gap-2">
            {/* Desktop keeps Login in the bar and styles it as a real button, so returning
                users have an obvious target without the hero needing to carry one. */}
            <button
              type="button"
              onClick={onGoLogin}
              className="hidden items-center gap-1.5 whitespace-nowrap rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-500 md:inline-flex"
            >
              {/* leading-none matters: the glyph is text-base, whose 24px line box is taller
                  than the button's 20px text line and drove the flex row to 40px against Get
                  started's 36px. Collapsing it to the glyph's own 16px lets the label govern,
                  so both buttons are the same height. */}
              <Icon name="login" className="text-base leading-none" />
              Login
            </button>
            <button
              type="button"
              onClick={() => onGoSignup("customer")}
              className="whitespace-nowrap rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
            >
              Get started
            </button>
            <button
              type="button"
              onClick={() => setMobileMenuOpen((open) => !open)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
              className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-slate-700 dark:text-gray-200 hover:text-blue-700 md:hidden"
            >
              <Icon name={mobileMenuOpen ? "close" : "menu"} className="text-2xl" />
            </button>
          </div>
        </div>
        {mobileMenuOpen ? (
          <nav aria-label="Mobile" className="border-t border-slate-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 px-4 py-3 md:hidden">
            <div className="flex flex-col gap-1">
              {/* Login lives here on mobile - it does not fit in the header bar alongside the
                  wordmark and the Get started CTA. */}
              <button type="button" onClick={() => { setMobileMenuOpen(false); onGoLogin(); }} className="rounded-lg px-2 py-2 text-left text-sm font-semibold text-slate-900 dark:text-gray-100 hover:text-blue-700">Login</button>
              <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} className="rounded-lg px-2 py-2 text-sm font-medium text-slate-700 dark:text-gray-200 hover:text-blue-700">How it works</a>
              <a href="#owners" onClick={() => setMobileMenuOpen(false)} className="rounded-lg px-2 py-2 text-sm font-medium text-slate-700 dark:text-gray-200 hover:text-blue-700">For owners</a>
              <a href="#shops" onClick={() => setMobileMenuOpen(false)} className="rounded-lg px-2 py-2 text-sm font-medium text-slate-700 dark:text-gray-200 hover:text-blue-700">For shops</a>
              <button type="button" onClick={() => { setMobileMenuOpen(false); onGoContact(); }} className="rounded-lg px-2 py-2 text-left text-sm font-medium text-slate-700 dark:text-gray-200 hover:text-blue-700">Contact us</button>
            </div>
          </nav>
        ) : null}
      </header>

      <main id="top">
        {/* Phones only: the header bar cannot fit Login alongside the wordmark and the Get
            started CTA, so it lives in the hamburger menu - which returning users have to open
            to find. This sits directly under the bar rather than inside the hero, whose py-16
            would push it a screenful down. Desktop has the header Login button instead. */}
        <div className="px-4 pt-4 md:hidden">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/40 px-4 py-3">
            <span className="text-sm font-medium text-slate-700 dark:text-gray-200">
              Already have an account?
            </span>
            <button
              type="button"
              onClick={onGoLogin}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-500"
            >
              <Icon name="login" className="text-base" />
              Log in here
            </button>
          </div>
        </div>

        {/* Hero */}
        <section className="relative overflow-hidden">
          {/* Light-mode hero wash: the opaque light gradient is gated to light mode so it
              does not paint a white band over the dark background. Dark mode keeps just the
              translucent color radials over the page's own dark surface. */}
          <div
            className="pointer-events-none absolute inset-0 -z-10 dark:hidden"
            style={{
              background:
                "radial-gradient(60% 60% at 15% 10%, rgba(29,78,216,0.10) 0%, rgba(29,78,216,0) 60%), radial-gradient(50% 50% at 95% 0%, rgba(124,58,237,0.10) 0%, rgba(124,58,237,0) 55%), linear-gradient(180deg, #f8fafc 0%, #ffffff 70%)",
            }}
          />
          <div
            className="pointer-events-none absolute inset-0 -z-10 hidden dark:block"
            style={{
              background:
                "radial-gradient(60% 60% at 15% 10%, rgba(59,130,246,0.14) 0%, rgba(59,130,246,0) 60%), radial-gradient(50% 50% at 95% 0%, rgba(139,92,246,0.14) 0%, rgba(139,92,246,0) 55%)",
            }}
          />
          {/* Phone top padding is trimmed because the login prompt above already separates the
              hero from the header; without it the two stack into a screenful of empty space.
              Breakpoints are spelled out rather than using py-*, so nothing depends on the
              order Tailwind happens to emit padding utilities in. */}
          <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-4 pb-16 pt-6 md:pt-16 lg:grid-cols-2 lg:pb-24 lg:pt-24">
            {/* Left: copy */}
            <div className="max-w-xl">
              <Pill className="bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300">
                <Icon name="bolt" className="text-base" />
                Local auto repair, simplified
              </Pill>
              <h1 className="mt-4 text-4xl font-bold leading-tight tracking-tight text-slate-900 dark:text-gray-100 md:text-5xl">
                Find the right shop for your car -{" "}
                <span className="text-blue-700 dark:text-blue-300">without the runaround.</span>
              </h1>
              <p className="mt-5 text-lg leading-relaxed text-slate-600 dark:text-gray-300">
                Send one request to several trusted local shops, compare quotes and response times,
                and book the best fit. Need them to come to you? Mobile mechanics are on the map too.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => onGoSignup("customer")}
                  className="rounded-lg bg-red-600 px-7 py-3 text-base font-semibold text-white shadow-sm hover:bg-red-500"
                >
                  Find a Shop
                </button>
                <button
                  type="button"
                  onClick={() => onGoSignup("shop")}
                  className="rounded-lg border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-7 py-3 text-base font-semibold text-blue-700 dark:text-blue-300 hover:bg-slate-50 dark:hover:bg-gray-700"
                >
                  Register Your Shop
                </button>
              </div>
              {/* Deliberately a text link, not a third button: three equal-weight CTAs next to
                  Find a Shop and Register Your Shop would dilute the main signup path. */}
              <p className="mt-4 text-sm text-slate-600 dark:text-gray-300">
                Not ready to sign up?{" "}
                <SpaLink
                  view="demo"
                  onNavigate={onNavigate}
                  className="font-semibold text-blue-700 dark:text-blue-300 hover:underline"
                >
                  Try the demo first
                </SpaLink>
                .
              </p>
              <p className="mt-4 flex items-center gap-2 text-sm text-slate-500 dark:text-gray-400">
                <Icon name="verified" className="text-base text-green-600 dark:text-green-400" />
                Free for vehicle owners · No obligation · Real shops near you
              </p>
            </div>

            {/* Right: code-built product preview */}
            <div className="relative">
              <div className="rounded-2xl border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900 p-4 shadow-xl">
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-900 dark:text-gray-100">Top matches near 55102</div>
                  <span className="rounded-full bg-blue-100 dark:bg-blue-950/40 px-2.5 py-0.5 text-[11px] font-semibold text-blue-700 dark:text-blue-300">
                    3 shops
                  </span>
                </div>
                <div className="space-y-2.5">
                  <PreviewShopCard
                    index={1}
                    name="Smith Auto"
                    location="Columbia Heights, MN"
                    score="92"
                    accent={SHOP_PIN_COLORS.shop}
                    badges={["General auto repair", "Acura specialist"]}
                  />
                  <PreviewShopCard
                    index={2}
                    name="Joe's Mobile Mechanic"
                    location="St Paul, MN"
                    score="88"
                    accent={SHOP_PIN_COLORS.mobile}
                    subline="Mobile · comes to you"
                    badges={["Mobile · comes to you", "General auto repair"]}
                  />
                  <PreviewShopCard
                    index={3}
                    name="Lakeside Service"
                    location="Minneapolis, MN"
                    score="81"
                    accent={SHOP_PIN_COLORS.shop}
                    badges={["Brake specialist"]}
                  />
                </div>
                {/* Faux map chip with a pin + violet coverage ring */}
                <div className="mt-3 overflow-hidden rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                  <div
                    className="relative h-24"
                    style={{
                      background:
                        "linear-gradient(135deg, #eef2ff 0%, #f1f5f9 45%, #faf5ff 100%)",
                    }}
                  >
                    <svg className="absolute inset-0 h-full w-full" viewBox="0 0 320 96" preserveAspectRatio="none" aria-hidden="true">
                      {/* mobile mechanic coverage ring */}
                      <circle cx="120" cy="48" r="34" fill={SHOP_PIN_COLORS.mobile} fillOpacity="0.10" />
                      <circle cx="120" cy="48" r="34" fill="none" stroke={SHOP_PIN_COLORS.mobile} strokeWidth="1.5" strokeDasharray="4 3" />
                      {/* a couple of shop pins */}
                      <circle cx="210" cy="40" r="5" fill={SHOP_PIN_COLORS.shop} stroke="#fff" strokeWidth="1.5" />
                      <circle cx="120" cy="48" r="5" fill={SHOP_PIN_COLORS.mobile} stroke="#fff" strokeWidth="1.5" />
                      <circle cx="250" cy="66" r="5" fill={SHOP_PIN_COLORS.shop} stroke="#fff" strokeWidth="1.5" />
                    </svg>
                    <span className="absolute bottom-2 left-2 rounded-full bg-white/90 dark:bg-gray-800/90 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:text-gray-300 shadow-sm">
                      Shops &amp; service areas near you
                    </span>
                  </div>
                </div>
              </div>
              {/* floating accent badge */}
              <div className="absolute -left-3 -top-3 hidden rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-xs font-semibold text-slate-700 dark:text-gray-200 shadow-md sm:block">
                <span className="text-blue-700 dark:text-blue-300">1 request</span> → many shops
              </div>
            </div>
          </div>
        </section>

        {/* Trust strip */}
        <section className="border-y border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900">
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-4 px-4 py-6 text-sm text-slate-600 dark:text-gray-300 md:grid-cols-4">
            {[
              ["forum", "Message several shops at once"],
              ["request_quote", "Compare quotes & response times"],
              ["build", "Specialists for your make"],
              ["local_shipping", "Mobile mechanics that come to you"],
            ].map(([icon, label]) => (
              <div key={label} className="flex items-center gap-2">
                <Icon name={icon} className="text-xl text-blue-700 dark:text-blue-300" />
                <span className="font-medium">{label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section id="how-it-works" className="bg-white dark:bg-gray-800 px-4 py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto mb-12 max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-gray-100">How it works</h2>
              <p className="mt-2 text-slate-600 dark:text-gray-300">Three steps from problem to fixed.</p>
            </div>

            {/* Demo walkthrough. Lazy facade, so the landing page does not pay for the YouTube
                player unless a visitor actually presses play. */}
            <div className="mx-auto mb-12 max-w-3xl">
              <VideoEmbed videoId="157UDc_HvSE" title="How GearCheckAuto works for vehicle owners" />
            </div>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {[
                ["edit_note", "1", "Request", "Describe what your car needs and send it to several nearby shops at once - no phone tag."],
                ["compare_arrows", "2", "Compare", "Get responses with quotes, specialties, response times, and completed-work history side by side."],
                ["event_available", "3", "Book", "Pick the shop that fits your budget and schedule - or one that comes to you - and book."],
              ].map(([icon, n, title, copy]) => (
                <div key={n} className="relative rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6">
                  <div className="mb-4 flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-700 text-base font-bold text-white">
                      {n}
                    </span>
                    <Icon name={icon} className="text-2xl text-blue-700 dark:text-blue-300" />
                  </div>
                  <h3 className="mb-1.5 text-lg font-semibold text-slate-900 dark:text-gray-100">{title}</h3>
                  <p className="text-sm leading-relaxed text-slate-600 dark:text-gray-300">{copy}</p>
                </div>
              ))}
            </div>
            <p className="mt-10 text-center text-sm text-slate-600 dark:text-gray-300">
              <SpaLink view="howItWorks" onNavigate={onNavigate} className="font-semibold text-blue-700 dark:text-blue-300 hover:underline">
                Read the full walkthrough
              </SpaLink>{" "}
              for what you need and answers to common questions.
            </p>
          </div>
        </section>

        {/* Why GearCheckAuto */}
        <section className="bg-slate-50 dark:bg-gray-900 px-4 py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto mb-12 max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-gray-100">Why GearCheckAuto</h2>
              <p className="mt-2 text-slate-600 dark:text-gray-300">
                Built to make finding the right shop fast, fair, and transparent.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <FeatureCard icon="send" title="One request, many shops">
                Submit once and reach multiple shops in your area - they come to you instead of you
                calling around.
              </FeatureCard>
              <FeatureCard icon="insights" title="Compare what matters">
                Weigh quotes, response speed, completed work orders, and make specialties before you
                commit.
              </FeatureCard>
              <FeatureCard icon="map" title="Find local on a map">
                Search by ZIP and radius and see shops near you on a map, with the details that help
                you choose.
              </FeatureCard>
              <FeatureCard
                icon="local_shipping"
                title="Mobile mechanics come to you"
                accent="text-violet-700 dark:text-violet-300"
                bg="bg-violet-100 dark:bg-violet-950/40"
              >
                Some shops travel to your driveway. Their service area shows right on the map so you
                know they reach you.
              </FeatureCard>
            </div>
          </div>
        </section>

        {/* Audience split */}
        <section className="bg-white dark:bg-gray-800 px-4 py-20">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 md:grid-cols-2">
            <div id="owners" className="rounded-2xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 shadow-sm">
              <div className="mb-5 flex items-center justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300">
                  <Icon name="directions_car" className="text-2xl" />
                </div>
                <Pill className="bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300">For vehicle owners</Pill>
              </div>
              <h3 className="mb-4 text-2xl font-bold text-slate-900 dark:text-gray-100">Get your car handled</h3>
              <ul className="mb-6 space-y-2.5 text-sm text-slate-600 dark:text-gray-300">
                {[
                  "Create a free account and add your vehicle details",
                  "Send one request to several shops and compare responses",
                  "Choose on price, speed, and specialty - then book",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <Icon name="check_circle" className="mt-0.5 text-base text-blue-700 dark:text-blue-300" />
                    {t}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => onGoSignup("customer")}
                className="rounded-lg bg-blue-700 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-600"
              >
                Book your service
              </button>
            </div>

            <div id="shops" className="rounded-2xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 shadow-sm">
              <div className="mb-5 flex items-center justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100 dark:bg-gray-900 text-amber-700 dark:text-amber-300">
                  <Icon name="precision_manufacturing" className="text-2xl" />
                </div>
                <Pill className="bg-amber-100 dark:bg-gray-900 text-amber-700 dark:text-amber-300">For shops</Pill>
              </div>
              <h3 className="mb-4 text-2xl font-bold text-slate-900 dark:text-gray-100">Grow your shop</h3>
              <ul className="mb-6 space-y-2.5 text-sm text-slate-600 dark:text-gray-300">
                {[
                  "Create a shop account and complete your profile",
                  "Set your specialties, service area, or mobile coverage",
                  "Get matched with nearby customers and message them directly",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <Icon name="check_circle" className="mt-0.5 text-base text-amber-700 dark:text-amber-300" />
                    {t}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => onGoSignup("shop")}
                className="rounded-lg bg-amber-600 px-6 py-3 text-sm font-semibold text-white hover:bg-amber-500"
              >
                Grow your business
              </button>
            </div>
          </div>
        </section>

        {/* Final CTA band */}
        <section className="px-4 pb-20">
          <div className="mx-auto max-w-7xl overflow-hidden rounded-2xl bg-blue-700 px-6 py-14 text-center shadow-xl">
            <h2 className="text-3xl font-bold text-white md:text-4xl">Ready to get back on the road?</h2>
            <p className="mx-auto mt-3 max-w-2xl text-lg text-blue-100">
              Join the drivers and shops using GearCheckAuto to connect faster and with less guesswork.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={() => onGoSignup("customer")}
                className="rounded-lg bg-white dark:bg-gray-800 px-7 py-3 text-base font-semibold text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40"
              >
                Get started
              </button>
              <button
                type="button"
                onClick={onGoContact}
                className="rounded-lg border border-blue-300 dark:border-blue-800 px-7 py-3 text-base font-semibold text-white hover:bg-blue-600"
              >
                Talk to us
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-4 py-12 md:grid-cols-4">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2">
              <img src="/logo-192.png" alt="" className="h-7 w-7 rounded-lg" />
              <span className="text-base font-bold text-slate-900 dark:text-gray-100">GearCheckAuto</span>
            </div>
            <p className="mt-3 max-w-sm text-sm text-slate-600 dark:text-gray-300">
              Connect with trusted local auto repair shops and mobile mechanics - compare, message,
              and book in one place.
            </p>
            {showAppLinks && <FooterStoreLinks />}
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900 dark:text-gray-100">Guides</div>
            <ul className="mt-3 space-y-2 text-sm text-slate-600 dark:text-gray-300">
              <li><SpaLink view="prePurchaseInspection" onNavigate={onNavigate} className="hover:text-blue-700">Pre-purchase inspections</SpaLink></li>
              <li><SpaLink view="autoRepair" onNavigate={onNavigate} className="hover:text-blue-700">Auto repair help</SpaLink></li>
              <li><SpaLink view="vehicleInspection" onNavigate={onNavigate} className="hover:text-blue-700">Vehicle inspections</SpaLink></li>
              <li><SpaLink view="faq" onNavigate={onNavigate} className="hover:text-blue-700">FAQ</SpaLink></li>
            </ul>
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900 dark:text-gray-100">Product</div>
            <ul className="mt-3 space-y-2 text-sm text-slate-600 dark:text-gray-300">
              <li><a href="#how-it-works" className="hover:text-blue-700">How it works</a></li>
              <li><a href="#owners" className="hover:text-blue-700">For owners</a></li>
              <li><a href="#shops" className="hover:text-blue-700">For shops</a></li>
            </ul>
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900 dark:text-gray-100">Get started</div>
            <ul className="mt-3 space-y-2 text-sm text-slate-600 dark:text-gray-300">
              <li><button type="button" onClick={onGoSignup} className="hover:text-blue-700">Sign up</button></li>
              <li><button type="button" onClick={onGoLogin} className="hover:text-blue-700">Log in</button></li>
              <li><button type="button" onClick={onGoContact} className="hover:text-blue-700">Contact</button></li>
              {showAppLinks && (
                <li><SpaLink view="mobileApp" onNavigate={onNavigate} className="hover:text-blue-700">Get the app</SpaLink></li>
              )}
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200 dark:border-gray-700">
          <div className="mx-auto max-w-7xl px-4 py-5 text-xs text-slate-500 dark:text-gray-400">
            © {year} GearCheckAuto. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
